import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, ExternalLink, Lock, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ComplianceOperationsHub({ authFetch }) {
  const [guide, setGuide] = useState(null);

  const loadGuide = useCallback(async () => {
    const res = await authFetch(`${API}/admin/compliance-operations-guide`);
    const data = await res.json();
    if (res.ok) setGuide(data);
  }, [authFetch]);

  useEffect(() => {
    loadGuide();
  }, [loadGuide]);

  if (!guide) {
    return <div className="glass-card p-8 text-center text-text-muted" data-testid="compliance-operations-loading">Cargando guía de compliance y operación...</div>;
  }

  return (
    <div className="space-y-6" data-testid="compliance-operations-hub">
      <div className="glass-card tracing-panel p-6" data-testid="compliance-ops-hero">
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Compliance + operaciones</p>
        <h3 className="mt-2 text-2xl font-semibold text-text-heading">Centro interno para dejar Neuralia369 lista para operar</h3>
        <p className="mt-3 text-sm text-text-body">Aquí tienes accesos, orden recomendado, pasos técnicos y checklist legal para cerrar integraciones sin perder control ni compliance en Europa.</p>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <div className="glass-card p-6" data-testid="compliance-ops-access-card">
          <div className="flex items-center gap-2 text-text-heading">
            <Lock className="w-5 h-5 text-primary" />
            <h4 className="text-lg font-semibold">Accesos actuales</h4>
          </div>
          <div className="mt-5 space-y-4">
            {Object.entries(guide.credentials || {}).map(([key, value]) => (
              <div key={key} className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid={`compliance-access-${key}`}>
                <p className="text-sm font-semibold text-text-heading">{key === 'admin_panel' ? 'Panel admin' : 'Panel partner QA'}</p>
                <p className="mt-2 text-sm text-text-body">Email: {value.email}</p>
                <p className="text-sm text-text-body">Password: {value.password}</p>
                <a href={value.url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 text-sm text-primary" data-testid={`compliance-access-link-${key}`}>
                  Abrir panel <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-card p-6" data-testid="compliance-ops-order-card">
          <div className="flex items-center gap-2 text-text-heading">
            <ShieldCheck className="w-5 h-5 text-primary" />
            <h4 className="text-lg font-semibold">Orden recomendado para activar todo</h4>
          </div>
          <div className="mt-5 space-y-3">
            {(guide.next_best_order || []).map((item, index) => (
              <div key={item} className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid={`compliance-next-order-${index}`}>
                <p className="text-sm text-text-body">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <div className="glass-card p-6" data-testid="compliance-ops-tech-checklist">
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Checklist técnico</p>
          <div className="mt-4 space-y-3">
            {(guide.operations_checklist || []).map((item, index) => (
              <div key={index} className="flex items-start gap-3 rounded-[18px] border border-border-subtle bg-background/55 p-4" data-testid={`compliance-tech-step-${index}`}>
                <CheckCircle2 className="w-4 h-4 mt-0.5 text-primary" />
                <p className="text-sm text-text-body">{item}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-card p-6" data-testid="compliance-ops-legal-checklist">
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Checklist legal / compliance</p>
          <div className="mt-4 space-y-3">
            {(guide.legal_checklist || []).map((item, index) => (
              <div key={index} className="flex items-start gap-3 rounded-[18px] border border-border-subtle bg-background/55 p-4" data-testid={`compliance-legal-step-${index}`}>
                <CheckCircle2 className="w-4 h-4 mt-0.5 text-primary" />
                <p className="text-sm text-text-body">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {Object.entries(guide.integrations || {}).map(([key, integration]) => (
          <div key={key} className="glass-card p-6" data-testid={`compliance-integration-${key}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Integración</p>
                <h4 className="mt-2 text-xl font-semibold text-text-heading">{integration.name}</h4>
              </div>
              <a href={integration.official_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-primary" data-testid={`compliance-integration-link-${key}`}>
                Abrir guía oficial <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            <div className="mt-5 grid gap-4 xl:grid-cols-4">
              <div className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`compliance-integration-creds-${key}`}>
                <p className="text-sm font-semibold text-text-heading">Credenciales</p>
                <div className="mt-3 space-y-2">
                  {(integration.credential_labels || []).map((item) => <p key={item} className="text-xs text-text-muted">• {item}</p>)}
                </div>
              </div>
              <div className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`compliance-integration-steps-${key}`}>
                <p className="text-sm font-semibold text-text-heading">Cómo se hace</p>
                <div className="mt-3 space-y-2">
                  {(integration.steps || []).map((item, index) => <p key={index} className="text-xs text-text-muted">{index + 1}. {item}</p>)}
                </div>
              </div>
              <div className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`compliance-integration-verify-${key}`}>
                <p className="text-sm font-semibold text-text-heading">Cómo verificar</p>
                <div className="mt-3 space-y-2">
                  {(integration.verify || []).map((item, index) => <p key={index} className="text-xs text-text-muted">• {item}</p>)}
                </div>
              </div>
              <div className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`compliance-integration-eu-${key}`}>
                <p className="text-sm font-semibold text-text-heading">Listo para Europa</p>
                <div className="mt-3 space-y-2">
                  {(integration.europe_ready || []).map((item, index) => <p key={index} className="text-xs text-text-muted">• {item}</p>)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}